import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Monitor } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  email: "",
  password: "",
  phone: "",
  address: "",
  property_type: "own",
  aadhaar_number: "",
};

const inputClass =
  "w-full bg-night border border-line px-4 py-3 text-sm text-ink focus:border-brand outline-none font-mono transition-colors";
const labelClass = "block font-mono text-xs uppercase text-mute mb-2";

const VendorRegister = () => {
  const [form, setForm] = useState(INITIAL);
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!front || !back) {
      toast.error("Please upload both Aadhaar front and back images.");
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      data.append("aadhaar_front", front);
      data.append("aadhaar_back", back);
      await axios.post(`${API}/vendor/register`, data);
      toast.success("Registration submitted. Our team will verify your documents.");
      setForm(INITIAL);
      setFront(null);
      setBack(null);
      e.target.reset();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Registration failed. Please verify all fields and retry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-night text-ink font-sans antialiased min-h-screen selection:bg-brand selection:text-night">
      <header className="bg-surface border-b border-line">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="register-brand-link">
            <Monitor className="w-6 h-6 text-brand" strokeWidth={2} />
            <span className="font-mono text-sm tracking-wider uppercase font-bold text-brand">
              DSS Tender Hub
            </span>
          </a>
          <a href="/vendor" className="font-mono text-xs uppercase text-mute hover:text-brand transition-colors" data-testid="register-login-link">
            Vendor Login →
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <p className="font-mono text-xs uppercase text-brand tracking-widest mb-2">Vendor Accreditation</p>
        <h1 className="font-display text-[clamp(32px,4vw,56px)] uppercase mb-4 tracking-wide">
          Tender Person Registration
        </h1>
        <p className="text-sm text-mute mb-10">
          Register with your Aadhaar verification to participate in warehouse tenders.
          Registrations are reviewed by our team before bidding access is granted.
        </p>

        <form onSubmit={handleSubmit} className="bg-surface border border-line p-8 space-y-6" data-testid="vendor-register-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={labelClass} htmlFor="vr-name">Full Name / Firm Name</label>
              <input type="text" id="vr-name" required value={form.name} onChange={update("name")} className={inputClass} placeholder="Your name or firm" data-testid="vr-name-input" />
            </div>
            <div>
              <label className={labelClass} htmlFor="vr-email">Email (your login)</label>
              <input type="email" id="vr-email" required value={form.email} onChange={update("email")} className={inputClass} placeholder="you@company.com" data-testid="vr-email-input" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={labelClass} htmlFor="vr-password">Password (min 8 characters)</label>
              <input type="password" id="vr-password" required minLength={8} value={form.password} onChange={update("password")} className={inputClass} placeholder="Choose a password" data-testid="vr-password-input" />
            </div>
            <div>
              <label className={labelClass} htmlFor="vr-phone">Phone</label>
              <input type="text" id="vr-phone" required value={form.phone} onChange={update("phone")} className={inputClass} placeholder="+91 ..." data-testid="vr-phone-input" />
            </div>
          </div>

          <div>
            <label className={labelClass} htmlFor="vr-address">Address</label>
            <textarea id="vr-address" required rows="2" value={form.address} onChange={update("address")} className={inputClass} placeholder="Full address with district and state" data-testid="vr-address-input" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={labelClass} htmlFor="vr-property">Warehouse Property</label>
              <select id="vr-property" value={form.property_type} onChange={update("property_type")} className={inputClass} data-testid="vr-property-select">
                <option value="own">Own</option>
                <option value="rented">Rented</option>
              </select>
            </div>
            <div>
              <label className={labelClass} htmlFor="vr-aadhaar">Aadhaar Number (stored masked: XXXX-XXXX-last4)</label>
              <input type="text" id="vr-aadhaar" required pattern="\d{12}" maxLength={12} value={form.aadhaar_number} onChange={update("aadhaar_number")} className={inputClass} placeholder="12-digit Aadhaar number" data-testid="vr-aadhaar-input" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={labelClass} htmlFor="vr-front">Aadhaar Front Photo</label>
              <input type="file" id="vr-front" required accept="image/*" onChange={(e) => setFront(e.target.files[0] || null)} className={`${inputClass} file:mr-3 file:bg-element file:border-0 file:text-ink file:font-mono file:text-xs file:px-3 file:py-1`} data-testid="vr-aadhaar-front-input" />
            </div>
            <div>
              <label className={labelClass} htmlFor="vr-back">Aadhaar Back Photo</label>
              <input type="file" id="vr-back" required accept="image/*" onChange={(e) => setBack(e.target.files[0] || null)} className={`${inputClass} file:mr-3 file:bg-element file:border-0 file:text-ink file:font-mono file:text-xs file:px-3 file:py-1`} data-testid="vr-aadhaar-back-input" />
            </div>
          </div>

          <p className="font-mono text-[11px] text-mute leading-relaxed" data-testid="vr-privacy-note">
            Your Aadhaar number is stored masked (last 4 digits only). Card images are kept
            securely and visible only to the verification team.
          </p>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-brand hover:bg-brandhover disabled:opacity-60 text-night font-mono text-xs font-bold uppercase tracking-widest py-4 transition-colors"
            data-testid="vr-submit-btn"
          >
            {submitting ? "Submitting..." : "Submit For Verification →"}
          </button>
        </form>
      </main>
    </div>
  );
};

export default VendorRegister;
